import { create } from 'zustand'
import { SiweMessage } from 'siwe'
import { api } from '@/lib/api'

interface User {
  id: string
  address: string
  isAdmin: boolean
  permissions?: {
    canApprove: boolean
    canReject: boolean
    canWithdraw: boolean
    canSchedule: boolean
    canConfigure: boolean
  }
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isAdmin: boolean              // backend-confirmed via JWT, ONLY true when JWT matches current wallet
  isAdminCandidate: boolean     // pre-SIWE: backend confirmed this address is in walletHolder
  adminCandidateChecked: boolean // flips to true after the first /auth/is-admin response — avoids
                                 // flashing "Acceso Denegado" while the check is in flight
  isLoading: boolean
  error: string | null

  signIn: (address: string, signMessage: (args: { message: string }) => Promise<string>) => Promise<boolean>
  signOut: () => void
  checkAuth: (currentAddress?: string) => Promise<void>
  refreshAdminCandidate: (address: string | undefined) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isAdminCandidate: false,
  adminCandidateChecked: false,
  isLoading: false,
  error: null,

  signIn: async (address: string, signMessage) => {
    set({ isLoading: true, error: null })
    try {
      const { nonce } = await api.getNonce(address)
      const siweMessage = new SiweMessage({
        domain: window.location.host,
        address: address,
        statement: 'Sign in to W2USDT Bridge',
        uri: window.location.origin,
        version: '1',
        chainId: 111888,
        nonce: nonce,
      })

      const message = siweMessage.prepareMessage()
      const signature = await signMessage({ message })
      const verifyResponse = await api.verifySignature(message, signature)
      api.setToken(verifyResponse.token)

      const user = await api.getMe()

      set({
        user,
        isAuthenticated: true,
        isAdmin: user?.isAdmin || false,
        isAdminCandidate: user?.isAdmin || false,
        isLoading: false,
      })
      return true
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error de autenticación'
      set({ error: errorMsg, isLoading: false })
      return false
    }
  },

  signOut: () => {
    api.setToken(null)
    set({ user: null, isAuthenticated: false, isAdmin: false, error: null })
  },

  /**
   * Validates the JWT against the currently connected wallet.
   * If the JWT's address differs from the connected wallet, drops the token —
   * fixes the "previous admin login persists across wallet changes" UX hole.
   */
  checkAuth: async (currentAddress?: string) => {
    const token = api.getToken()
    if (!token) {
      set({ isAuthenticated: false, isAdmin: false, user: null })
      return
    }

    try {
      const user = await api.getMe()
      const sameWallet =
        !currentAddress ||
        (user?.address || '').toLowerCase() === currentAddress.toLowerCase()

      if (!sameWallet) {
        api.setToken(null)
        set({ isAuthenticated: false, isAdmin: false, user: null })
        return
      }

      set({ user, isAuthenticated: true, isAdmin: user?.isAdmin || false })
    } catch {
      api.setToken(null)
      set({ isAuthenticated: false, isAdmin: false, user: null })
    }
  },

  /**
   * Pre-SIWE check: asks the backend whether this address is an admin in the
   * walletHolder table. Drives the menu button visibility before any signing.
   */
  refreshAdminCandidate: async (address: string | undefined) => {
    if (!address) {
      set({ isAdminCandidate: false, adminCandidateChecked: true })
      return
    }
    set({ adminCandidateChecked: false })
    try {
      const { isAdmin } = await api.checkIsAdmin(address)
      set({ isAdminCandidate: isAdmin, adminCandidateChecked: true })
    } catch {
      set({ isAdminCandidate: false, adminCandidateChecked: true })
    }
  },
}))
